// src/pages/Utilisateurs/PrepaPhysique/utils/prepaPhysiqueUtils.js

export const PREPA_PHYSIQUE_STORAGE_KEY = "prepa_physique_user_ui_state";

export const DEFAULT_PREPA_PHYSIQUE_STATE = {
  search: "",
  status: "all", // all | todo | done
  programme: "Tous les programmes",
};

export const STATUS_OPTIONS = [
  {
    value: "all",
    label: "Tous",
  },
  {
    value: "todo",
    label: "À faire",
  },
  {
    value: "done",
    label: "Réalisés",
  },
];

export const collatorFR = new Intl.Collator("fr", {
  sensitivity: "base",
});

export function strip(value = "") {
  if (value === null || value === undefined) return "";

  return String(value)
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase()
    .trim();
}

export function toAbsoluteMediaUrl(url) {
  if (!url) return "";

  const value = String(url).trim();

  if (
    /^https?:\/\//i.test(value) ||
    value.startsWith("data:") ||
    value.startsWith("blob:")
  ) {
    return value;
  }

  if (!value.startsWith("/uploads")) return value;

  const base = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";

  return `${base}${value}`;
}

export function plainTextFromHtml(html = "") {
  if (!html) return "";

  return String(html)
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "")
    .replace(/<style[\s\S]*?>[\s\S]*?<\/style>/gi, "")
    .replace(/<p><br><\/p>/gi, " ")
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/<[^>]*>/g, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/\s+/g, " ")
    .trim();
}

export function truncateText(value = "", maxLength = 140) {
  const clean = plainTextFromHtml(value);

  if (!clean) return "";

  if (clean.length <= maxLength) return clean;

  return `${clean.slice(0, maxLength).trim()}…`;
}

export function formatDateFR(date) {
  if (!date || date === "0000-00-00") return "—";

  const parsed = new Date(date);

  if (Number.isNaN(parsed.valueOf())) return "—";

  return parsed.toLocaleDateString("fr-FR");
}

export function getAdherentFullName(adherent) {
  return [adherent?.prenom, adherent?.nom].filter(Boolean).join(" ").trim();
}

export function getExerciseDone(exercise) {
  return !!exercise?.AdherentPrepaPhysique?.is_done;
}

export function getExerciseRepetitions(exercise) {
  return Number(exercise?.AdherentPrepaPhysique?.repetitions || 0);
}

export function getExerciseRealisationDate(exercise) {
  return exercise?.AdherentPrepaPhysique?.date_realisation || "";
}

export function getExerciseProgramme(exercise) {
  return String(exercise?.programme || "").trim();
}

export function flattenExercises(exercisesByAdherent = {}) {
  return Object.values(exercisesByAdherent)
    .flat()
    .filter(Boolean);
}

export function buildPrepaDynamicOptions(exercisesByAdherent = {}) {
  const allExercises = flattenExercises(exercisesByAdherent);

  const programmes = Array.from(
    new Set(
      allExercises
        .map((exercise) => getExerciseProgramme(exercise))
        .filter(Boolean)
    )
  ).sort((a, b) => collatorFR.compare(a, b));

  return {
    programmes: [DEFAULT_PREPA_PHYSIQUE_STATE.programme, ...programmes],
  };
}

export function computePrepaStats(exercisesByAdherent = {}) {
  const allExercises = flattenExercises(exercisesByAdherent);

  const total = allExercises.length;
  const done = allExercises.filter((exercise) => getExerciseDone(exercise)).length;
  const todo = Math.max(total - done, 0);
  const progress = total > 0 ? Math.round((done / total) * 100) : 0;

  return {
    total,
    done,
    todo,
    progress,
  };
}

export function getExerciseSearchHaystack(exercise) {
  return [
    exercise?.nom,
    exercise?.programme,
    plainTextFromHtml(exercise?.description),
    exercise?.lien_photo,
    exercise?.lien_video,
  ]
    .map((value) => strip(value))
    .join(" ");
}

export function hasActivePrepaFilters(ui) {
  const safeUi = {
    ...DEFAULT_PREPA_PHYSIQUE_STATE,
    ...(ui || {}),
  };

  return (
    Boolean(safeUi.search) ||
    safeUi.status !== DEFAULT_PREPA_PHYSIQUE_STATE.status ||
    safeUi.programme !== DEFAULT_PREPA_PHYSIQUE_STATE.programme
  );
}

export function filterPrepaAdherents(
  adherents = [],
  exercisesByAdherent = {},
  ui
) {
  const safeAdherents = Array.isArray(adherents) ? adherents.filter(Boolean) : [];

  const safeUi = {
    ...DEFAULT_PREPA_PHYSIQUE_STATE,
    ...(ui || {}),
  };

  const term = strip(safeUi.search);

  return safeAdherents
    .map((adherent) => {
      const exercises = Array.isArray(exercisesByAdherent[adherent.id])
        ? exercisesByAdherent[adherent.id].filter(Boolean)
        : [];

      const adherentName = getAdherentFullName(adherent);
      const adherentMatches = term && strip(adherentName).includes(term);

      const filteredExercises = exercises.filter((exercise) => {
        const matchSearch =
          !term ||
          adherentMatches ||
          getExerciseSearchHaystack(exercise).includes(term);

        const isDone = getExerciseDone(exercise);

        const matchStatus =
          safeUi.status === "all" ||
          (safeUi.status === "done" && isDone) ||
          (safeUi.status === "todo" && !isDone);

        const matchProgramme =
          safeUi.programme === DEFAULT_PREPA_PHYSIQUE_STATE.programme ||
          getExerciseProgramme(exercise) === safeUi.programme;

        return matchSearch && matchStatus && matchProgramme;
      });

      const hasNoFilters = !hasActivePrepaFilters(safeUi);

      return {
        adherent,
        exercices: filteredExercises,
        totalBeforeFilter: exercises.length,
        visible: hasNoFilters || filteredExercises.length > 0 || adherentMatches,
      };
    })
    .filter((item) => item.visible);
}

export function computeAdherentProgress(exercices = []) {
  const safeExercises = Array.isArray(exercices) ? exercices.filter(Boolean) : [];

  const total = safeExercises.length;

  if (total === 0) {
    return {
      total: 0,
      done: 0,
      todo: 0,
      progress: 0,
    };
  }

  const done = safeExercises.filter((exercise) => getExerciseDone(exercise)).length;
  const todo = Math.max(total - done, 0);
  const progress = Math.round((done / total) * 100);

  return {
    total,
    done,
    todo,
    progress,
  };
}